// Shared mimetype → icon-family routing.
//
// `FileAttachment` and `FileUpload` both need to render a family glyph
// for the same file. Keeping the routing in one module stops the two
// consumers drifting — the original implementation had two near-
// identical copies with the same extension list.

export type FileFamily =
  | "document"
  | "spreadsheet"
  | "archive"
  | "code"
  | "image"
  | "video"
  | "generic";

const CODE_EXTENSIONS = [
  "ts",
  "tsx",
  "js",
  "jsx",
  "py",
  "rs",
  "go",
  "java",
  "c",
  "cpp",
  "h",
  "hpp",
  "sh",
  "rb",
  "swift",
  "kt",
  "css",
  "html",
  "json",
  "yaml",
  "yml",
  "toml",
] as const;

/**
 * Pick a family for a file. Mimetype wins when present; filename
 * extension is the fallback (uploads without a reliable mimetype are
 * routine). `image` / `video` are returned as families even though
 * `FileAttachment` doesn't render distinct glyphs for them — the
 * upload progress row does, and callers that don't want the media
 * branches can collapse them into `"generic"` at the call site.
 */
export function fileFamilyFor(name: string, mimetype: string | undefined): FileFamily {
  const mt = (mimetype ?? "").toLowerCase();
  if (mt.startsWith("image/")) return "image";
  if (mt.startsWith("video/")) return "video";
  if (
    mt === "application/pdf" ||
    mt.startsWith("application/vnd.openxmlformats-officedocument.wordprocessingml") ||
    mt === "application/msword" ||
    mt.startsWith("text/")
  ) {
    if (matchesExtension(name, CODE_EXTENSIONS)) return "code";
    return "document";
  }
  if (
    mt.startsWith("application/vnd.openxmlformats-officedocument.spreadsheetml") ||
    mt === "application/vnd.ms-excel" ||
    mt === "text/csv"
  ) {
    return "spreadsheet";
  }
  if (
    mt === "application/zip" ||
    mt === "application/x-zip-compressed" ||
    mt === "application/x-tar" ||
    mt === "application/gzip" ||
    mt === "application/x-7z-compressed" ||
    mt === "application/x-rar-compressed"
  ) {
    return "archive";
  }
  // Extension-based fallback for missing / opaque mimetypes. Order
  // here mirrors the mimetype branches above so both paths agree on
  // a single family for any (name, mimetype) pair.
  const ext = name.toLowerCase().split(".").pop() ?? "";
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(ext)) return "document";
  if (["xls", "xlsx", "csv", "numbers"].includes(ext)) return "spreadsheet";
  if (["zip", "tar", "gz", "tgz", "7z", "rar"].includes(ext)) return "archive";
  if (matchesExtension(name, CODE_EXTENSIONS)) return "code";
  return "generic";
}

function matchesExtension(name: string, extensions: readonly string[]): boolean {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  return extensions.includes(ext);
}
